import { useTranslate } from "helpers";
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import { ContactAddressCardProps, ContactAddressRef } from "./types";
import ContactAddressEditCard from "../ContactAddressEditCard/ContactAddressEditCard";
import { deleteContactAddressAPI, getContactAddressesAPI } from "services/api/contacts/api";
import { ContactAddressRow } from "services/api/contacts/types";
import { Dialog } from "ui/components/Dialog";
import { Tabs } from "ui/components/Tabs";
import ContactAddressMapTab from "./components/ContactAddressMapTab/ContactAddressMapTab";
import ContactAddressSearchTab from "./components/ContactAddressSearchTab/ContactAddressSearchTab";

const ContactAddressCard = forwardRef<ContactAddressRef, ContactAddressCardProps>((props, ref) => {
  const { contactDetail } = props;

  const { t } = useTranslate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<ContactAddressRow>
  >(initialDataWithPagination);

  const [addressId, setAddressId] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    if (contactDetail.id)
      fetchSearchResult(searchResults.pagination, contactDetail.id);
  }, [contactDetail.id]);

  const fetchSearchResult = async (pagination: Pagination, contactId: string) => {
    setIsLoading(true);
    try {
      const results = await getContactAddressesAPI(pagination, contactId);

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    if (id) {
      setAddressId(id);
      setShowAddNewModal(true);
    }
  }, []);

  const openAddNew = () => {
    setAddressId("");
    setShowAddNewModal(true);
  };

  const handleEditSuccess = useCallback(() => {
    if (contactDetail.id)
      fetchSearchResult(searchResults.pagination, contactDetail.id);
    setShowAddNewModal(false)
  }, [contactDetail.id, searchResults.pagination]);


  const handleDeleteClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();

    setAddressId(id);
    setOpenDialog(true);
  }

  const handleDeleteDialogConfirm = useCallback(async () => {
    if (!addressId) return;

    try {
      if (contactDetail.id) {
        const deleted = await deleteContactAddressAPI(
          t,
          contactDetail.id,
          addressId,
        );

        if (deleted) {
          await fetchSearchResult(searchResults.pagination, contactDetail.id);
        }
      }

      // zatvori dijalog nakon brisanja
      setOpenDialog(false);
      setAddressId("");
    } catch (err) {
      console.error("Greška prilikom brisanja email-a:", err);
    }
  }, [addressId, t, contactDetail.id, searchResults.pagination]);

  const handleDeleteDialogClose = useCallback(() => {
    setAddressId("");
    setOpenDialog(false);
  }, []);

  const handleGoogleMapClick = useCallback(
    (
      event: React.MouseEvent<HTMLDivElement>,
      latitude: number | null,
      longitude: number | null
    ) => {
      event.stopPropagation();
      event.preventDefault();
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`
      window.open(googleMapsUrl, "_blank", "noopener,noreferrer");
    },
    []
  );

  const handleWhatsAppClick = useCallback(
    (
      event: React.MouseEvent<HTMLDivElement>,
      address: string,
      latitude: number | null,
      longitude: number | null
    ) => {
      event.stopPropagation();
      if (latitude === null || longitude === null) return;

      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
      const message = `${address}\n${googleMapsUrl}`;
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, "_blank");
    },
    []
  );

  const handleViberClick = useCallback(
    (
      event: React.MouseEvent<HTMLDivElement>,
      address: string,
      latitude: number | null,
      longitude: number | null
    ) => {
      event.stopPropagation();
      if (latitude === null || longitude === null) return;

      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
      const message = `${address}\n${googleMapsUrl}`;
      const viberUrl = `viber://forward?text=${encodeURIComponent(message)}`;
      window.open(viberUrl, "_blank");
    },
    []
  );

  const handleCopyLinkClick = useCallback(
    (
      event: React.MouseEvent<HTMLDivElement>,
      latitude: number | null,
      longitude: number | null
    ) => {
      event.stopPropagation();
      if (latitude === null || longitude === null) return;

      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
      navigator.clipboard.writeText(googleMapsUrl).then(() => {
        // Možeš dodati toast notification ovdje
        alert("Link kopiran!");
      });
    },
    []
  );

  useImperativeHandle(ref, () => ({
    openAddNew,
  }));

  if (!contactDetail.id) return <></>;


  return (
    <>
      <Tabs
        tabs={[
          {
            label: t("admin.contact.address.tab.map"),
            content: (
              <div className="h-full w-full">
                <ContactAddressMapTab
                  searchResults={searchResults}
                  isLoading={isLoading}
                  onRowClick={handleRowClick}
                  onDeleteClick={handleDeleteClick}
                  onGoogleMapClick={handleGoogleMapClick}
                />
              </div>
            ),
          },
          {
            label: t("admin.contact.address.tab.search"),
            content: (
              <div>
                <ContactAddressSearchTab
                  searchResults={searchResults}
                  isLoading={isLoading}
                  onRowClick={handleRowClick}
                  onGoogleMapClick={handleGoogleMapClick}
                  onWhatsAppClick={handleWhatsAppClick}
                  onViberClick={handleViberClick}
                  onCopyLinkClick={handleCopyLinkClick}
                  onDeleteClick={handleDeleteClick}
                />
              </div>
            ),
          },
        ]}
      />

      <ContactAddressEditCard
        addressId={addressId}
        contactId={contactDetail.id}
        show={showAddNewModal}
        onSuccess={handleEditSuccess}
        onClose={() => setShowAddNewModal(false)}
      />
      <Dialog
        open={openDialog}
        title={t(
          "admin.contact.address.dialog.delete.title"
        )}
        message={t(
          "admin.contact.address.dialog.delete.message"
        )}
        confirmText={t(
          "admin.contact.address.dialog.delete.confirmtext"
        )}
        cancelText={t(
          "admin.contact.address.dialog.delete.canceltext"
        )}
        onConfirm={handleDeleteDialogConfirm}
        onCancel={handleDeleteDialogClose}
      />
    </>
  );
});

export default memo(ContactAddressCard);

